import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

// components
import HookForm from '../../components/HookForm/HookForm';
import Button from '../../components/Button/Button';
import Loader from '../../components/Loader/Loader';
import SelectStatuses from '../../components/Selects/SelectStatuses';
import SelectManager from '../../components/Selects/SelectManager';
import LeadCarCard from './LeadCarCard';

// icons
import phoneIcon from '../../brand/generated/input/phone.svg';

// api
import getLeadCard from '../../api/leads/getLeadCard';
import editLead from '../../api/leads/editLead';
import acceptLead from '../../api/leads/acceptLead';
import { ROLE_MANAGER } from '../../constants/roles';
import addLeadComment from '../../api/leads/addLeadComment';
import checkCrmSidebarNotifications from '../../api/notifications/checkCrmSidebarNotifications';
import alert from '../../helpers/alert';
import formatDate from '../../utils/formatDate';
import fixDateTimezone from '../../utils/fixDateTimezone';

const formStyles = {
  leadInfo: { display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(4, 1fr)' },
};

const styles = {
  card: { background: '#fff', border: '1px solid #E7E7ED', borderRadius: 8, padding: 24, marginTop: 20 },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 },
  cardTitle: { fontWeight: 600, fontSize: 15 },
  counter: {
    fontSize: 11,
    fontWeight: 600,
    color: '#8B8B8B',
    background: '#F4F4F6',
    borderRadius: 10,
    padding: '2px 8px',
  },
  commentList: { display: 'grid', gap: 12, marginBottom: 20 },
  comment: { borderLeft: '2px solid var(--brand-primary)', background: '#FAFAFB', borderRadius: '0 4px 4px 0', padding: '10px 14px' },
  commentMeta: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 },
  commentAuthor: { fontSize: 12, fontWeight: 600, color: '#2E3135' },
  commentDate: { fontSize: 11, color: '#8B8B8B' },
  commentText: { fontSize: 13, lineHeight: '20px', color: '#2E3135', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  empty: { color: '#8B8B8B', fontSize: 13, marginBottom: 20 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'stretch' },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E7E7ED',
    borderRadius: 4,
    fontFamily: 'inherit',
    fontSize: 13,
    lineHeight: '20px',
    color: '#2E3135',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  formActions: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 },
  hint: { fontSize: 10, color: '#B4B4B4' },
  acceptBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    background: '#FAFAFB',
    border: '1px solid #E7E7ED',
    borderRadius: 8,
    padding: '12px 24px',
    marginTop: 20,
    marginBottom: 20,
  },
};

const asOption = (id, label) => (id ? { value: id, label } : undefined);

const LeadMainInfo = () => {
  const { id } = useParams();
  const [lead, setLead] = useState();
  const [loading, setLoading] = useState(true);
  // Карточка открывается в режиме просмотра; карандаш у заголовка включает правку —
  // раньше он не делал ничего, и поменять что-либо из карточки было нельзя.
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState('');
  // Рядовой менеджер лиды не раздаёт — ответственного ему ставит только кнопка «Принять в работу».
  const isManager = useSelector((state) => state?.user?.roleId) === ROLE_MANAGER;

  const load = () => {
    return getLeadCard({ id })
      .then((res) => {
        setLead(res.data);
        if (res.data?.notifications?.length > 0) {
          checkCrmSidebarNotifications({
            notificationIds: res.data.notifications.map((item) => item.id),
          }).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    load();
  }, [id]);

  const handleSubmit = (values) => {
    // Шлём только изменённые поля. editLead применяет всё, что пришло не-null, а на
    // statusId=9 заводит сделку — лишний раз этот статус отправлять не надо.
    const payload = { id: Number(id) };
    if (values.fio !== lead?.fio) payload.fio = values.fio;
    if (values.phone !== lead?.phone) payload.phone = values.phone;
    if (values.statusId?.value && values.statusId.value !== lead?.status?.id)
      payload.statusId = values.statusId.value;
    if (values.managerId?.value && values.managerId.value !== lead?.manager?.id)
      payload.managerId = values.managerId.value;

    if (Object.keys(payload).length === 1) {
      setEditMode(false);
      return;
    }

    setSaving(true);
    editLead(payload)
      .then(() => {
        alert.success('Лид обновлён');
        setEditMode(false);
        return load();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const handleAccept = () => {
    setSaving(true);
    acceptLead(Number(id))
      .then(() => {
        alert.success('Лид принят в работу');
        window.dispatchEvent(new Event('crmSidebarRefresh'));
      })
      .catch(() => {})
      // И при отказе («уже взят другим») карточку перечитываем — она устарела.
      .then(load)
      .finally(() => setSaving(false));
  };

  const handleAddComment = () => {
    const text = comment.trim();
    if (!text) return;
    setSaving(true);
    addLeadComment({ leadId: Number(id), comment: text })
      .then(() => {
        setComment('');
        return load();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  if (loading) return <Loader />;

  const inputs = [
    {
      name: 'createdAt',
      label: 'Дата',
      defaultValue: lead?.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '',
      disabled: true,
    },
    { name: 'fio', label: 'Имя', defaultValue: lead?.fio || '', disabled: !editMode },
    {
      name: 'source',
      label: 'Источник',
      defaultValue: lead?.source?.title || '',
      disabled: true,
    },
    {
      name: 'phone',
      label: 'Телефон',
      icon: phoneIcon,
      defaultValue: lead?.phone || '',
      disabled: !editMode,
    },
    { name: 'mark', label: 'Марка автомобиля', defaultValue: lead?.mark?.name || '', disabled: true },
    { name: 'model', label: 'Модель автомобиля', defaultValue: lead?.model?.name || '', disabled: true },
    {
      name: 'statusId',
      component: SelectStatuses,
      label: 'Статус',
      defaultValue: asOption(lead?.status?.id, lead?.status?.title),
      disabled: !editMode,
    },
    {
      name: 'managerId',
      component: SelectManager,
      label: 'Менеджер',
      defaultValue: asOption(lead?.manager?.id, lead?.manager?.fullName),
      disabled: !editMode || isManager,
    },
  ];

  return (
    <>
      {!lead?.manager && (
        <div style={styles.acceptBar}>
          <span>У лида пока нет ответственного менеджера</span>
          <Button text="Принять в работу" fill disabled={saving} onClick={handleAccept} />
        </div>
      )}
      <HookForm
        // key — чтобы при выходе из правки поля перечитались из свежего лида
        key={`lead-${id}-${editMode}-${lead?.status?.id}-${lead?.manager?.id}`}
        inputs={inputs}
        title={'Информация о лиде'}
        grid={formStyles.leadInfo}
        border
        edit
        handleFormEdit={() => setEditMode(!editMode)}
        action={editMode ? 'Сохранить' : undefined}
        loading={saving}
        onSubmit={handleSubmit}
      />

      <LeadCarCard lead={lead} onChanged={load} />

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <p style={styles.cardTitle}>Заметки о лиде</p>
          {lead?.comments?.length > 0 && <span style={styles.counter}>{lead.comments.length}</span>}
        </div>

        {lead?.comments?.length > 0 ? (
          <div style={styles.commentList}>
            {lead.comments.map((item) => (
              <div key={item.id} style={styles.comment}>
                <div style={styles.commentMeta}>
                  <span style={styles.commentAuthor}>{item.author || 'Автор не указан'}</span>
                  {item.createdAt && (
                    <span style={styles.commentDate}>{formatDate(fixDateTimezone(item.createdAt), true)}</span>
                  )}
                </div>
                <p style={styles.commentText}>{item.comment}</p>
              </div>
            ))}
          </div>
        ) : (
          <p style={styles.empty}>Заметок пока нет — первая появится здесь.</p>
        )}

        <div style={styles.formRow}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              // Ctrl/Cmd+Enter — привычная отправка, чтобы не тянуться мышью к кнопке
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAddComment();
            }}
            placeholder="Оставьте комментарий о лиде"
            rows={3}
            style={styles.textarea}
          />
          <div style={styles.formActions}>
            <Button text="Добавить" fill disabled={saving || !comment.trim()} onClick={handleAddComment} />
            <span style={styles.hint}>Ctrl + Enter</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadMainInfo;
